<template>
    <div id="divMobileUsageStatement" class="container top20" v-show="usageStatementDataLoaded" v-cloak>

        <!-- Service Location -->
        <div class="row">
            <div class="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                <label for="ddlServiceLocations" class="lbl control-label">{{ $t("message.serviceLocation") }}</label>      
                <select id="ddlServiceLocations" v-model="selectedServiceLocation" class="dropdown form-control" 
                                    @change="getServicePoints(selectedServiceLocation)" :disabled="availableServiceLocations.length === 1">
                    <option v-for="(sl, index) in availableServiceLocations" :key="sl.ServiceLocationID" :value="sl" :selected="index === 0">{{ sl.Address1 }}</option>
                </select>
            </div>
        </div>

        <!-- Service Points/Premises -->
        <!-- 2.5.X version will need Service Points/Premises dropdown as Billing Cycle data is tied to Service Point -->
        <div class="row top20" v-if="showServicePoints">
            <div class="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                <label for="ddlServicePoints" class="lbl control-label">{{ $tc("message.servicePoints", servicePoints.length) }}</label>      
                <select id="ddlServicePoints" v-model="selectedServicePoint" class="dropdown form-control" 
                                    @change="getUsageStatementDates(selectedServicePoint)" :disabled="servicePoints.length === 1">
                    <option v-for="(sp, index) in servicePoints" :key="sp.PremiseId" :value="sp" :selected="index === 0">{{ sp.Address }}</option>
                </select>
            </div>
        </div>

        <!-- Statement Dates -->
        <div class="row top20">
            <div class="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                <label for="ddlStatementDates" class="lbl control-label">{{ $t("message.statementDates") }}</label>
                <select id="ddlStatementDates" v-model="selectedUsageStatementDate" class="dropdown form-control" 
                                    @change="getServicePointsUsageData(selectedUsageStatementDate)" :disabled="usageStatementDates.length === 1">
                    <option v-for="(statementDate, index) in usageStatementDates" :key="statementDate.BillingCycleAssignmentHistoryID" :value="statementDate" :selected="index === 0">
                        {{ statementDate.CycleStartDate }} - {{ statementDate.CycleEndDate }}
                    </option>
                </select>
            </div>
        </div>

        <!-- Usage Content by Service Point -->
        <div id="divUsageContentByServicePoint" class="row top20" v-show="showUsageContentByServicePoint && servicePointUsageData !== null">
            <!-- Current Charges -->
            <div class="col-lg-12">
                <div id="divUsageTotal">
                    <label><b>{{ $t("message.currentCharges") }}</b></label><br />
                    <table cellspacing="0" class="table table-striped">
                        <tbody>
                            <tr>
                                <td class="usageBreakdown">
                                    {{ $t("message.usageCharge") }}
                                </td>
                                <td class="usageBreakdownNumber">
                                    {{ $n(consumptionSubTotal.toFixed(2), 'currency') }}
                                </td>
                            </tr>
                            <tr>
                                <td class="usageBreakdown">
                                    {{ $t("message.miscAdjustments") }}
                                </td>
                                <td class="usageBreakdownNumber">
                                    {{ $n(miscSubTotal.toFixed(2), 'currency') }}
                                </td>
                            </tr>
                            <tr class="totalBar">
                                <td class="usageBreakdown"> 
                                    {{ $t("message.totalCurrentCharges") }}
                                </td>
                                <td class="usageBreakdownNumber">
                                    {{ $n((consumptionSubTotal + miscSubTotal).toFixed(2), 'currency') }}
                                </td>
                            </tr>
                        </tbody>                        
                    </table>
                </div>
            </div>

            <!-- Tabs: Usage, Charges and Adjustments -->
            <div class="col-lg-12">
                <div class="usage-content">
                    <tabs :options="{ useUrlFragment: false, defaultTabHash: 'usage' }">
                        <tab id="usage" name="Usage">
                            <div class="tab-pane-header text-center">
                                <label><b>{{ servicePointUsageData.ServiceType }} {{ $t("message.usage") }}</b></label><br />
                            </div>
                            <div v-if="usage.length > 0">
                                <table cellspacing="0" class="table table-striped" v-for="usageDetails in usage" :key="usageDetails">
                                    <tbody>
                                        <tr>
                                            <td><b>{{ $t("message.meterNum") }}:</b> {{ usageDetails.SerialNo }}</td>
                                        </tr>
                                        <tr>
                                            <td><b>{{ $t("message.servicePeriod") }}:</b> {{ usageDetails.ServicePeriod }}</td>
                                        </tr>
                                        <tr>
                                            <td><b>{{ $t("message.daysOfService") }}:</b> {{ usageDetails.DaysOfService }}</td>
                                        </tr>
                                        <tr>
                                            <td><b>{{ $t("message.startReading") }}:</b> {{ usageDetails.StartRead }}</td>
                                        </tr>
                                        <tr>
                                            <td><b>{{ $t("message.endReading") }}:</b> {{ usageDetails.EndRead }}</td>
                                        </tr>
                                        <tr>
                                            <td><b>{{ $t("message.unitsUsed") }}:</b> {{ (usageDetails.EndRead - usageDetails.StartRead).toFixed(6) }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div v-else>
                                <label>{{ $t("message.noDataAvailable") }}</label>
                            </div> 
                        </tab>

                        <tab id="charges" name="Charges">
                            <div class="tab-pane-header text-center">
                                <label><b>{{ $t("message.usageCharge") }}</b></label><br />
                            </div>
                            <div v-if="consumptionCharges.length > 0">
                                <table cellspacing="0" class="table table-striped">
                                    <tbody>
                                        <tr class="titleRow">
                                            <th>{{ $t("message.rateDescription") }}</th>
                                            <th>&nbsp;</th>
                                            <th class="text-right">{{ $t("message.amountCharged") }}</th>
                                        </tr>
                                        <tr v-for="usageConsumptionCharges in consumptionCharges" :key="usageConsumptionCharges">
                                            <td>
                                                <b>{{ usageConsumptionCharges.Name }}</b>
                                            </td>
                                            <td class="tableColumn2">
                                                {{ usageConsumptionCharges.RateDescription }}
                                            </td>
                                            <td class="text-right">
                                                {{ handleNegativeNumber($n(usageConsumptionCharges.TotalAmount.toFixed(6), 'currency6Decimal')) }}
                                            </td>
                                        </tr>
                                        <tr>                                            
                                            <td colspan="2" class="tableColumn2">
                                                <b>{{ $t("message.subtotal") }}</b>
                                            </td>
                                            <td class="text-right">
                                                <b>{{ handleNegativeNumber($n(consumptionSubTotal.toFixed(6), 'currency6Decimal')) }}</b>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div v-else>
                                <label>{{ $t("message.noDataAvailable") }}</label>
                            </div>
                        </tab>

                        <tab id="adjustments" name="Adjustments">
                            <div class="tab-pane-header text-center">
                                <label><b>{{ $t("message.miscAdjustments") }}</b></label><br />
                            </div>
                            <div v-if="miscAdjustments.length > 0">
                                <table cellspacing="0" class="table table-striped">
                                    <tbody>
                                        <tr class="titleRow">
                                            <th>{{ $t("message.itemAmount") }}</th>
                                            <th>&nbsp;</th>
                                            <th class="text-right">{{ $t("message.amountCharged") }}</th>
                                        </tr>
                                        <tr v-for="usageMiscCharges in miscAdjustments" :key="usageMiscCharges">
                                            <td>
                                                <b>{{ usageMiscCharges.Name }}</b>
                                            </td>
                                            <td class="tableColumn2">
                                                {{ usageMiscCharges.RateDescription }}
                                            </td>
                                            <td class="text-right">
                                                {{ handleNegativeNumber($n(usageMiscCharges.TotalAmount.toFixed(6), 'currency6Decimal')) }}
                                            </td>
                                        </tr>
                                        <tr class="titleRow">
                                            <td colspan="2" class="tableColumn2 text-left">
                                                <b>{{ $t("message.subtotal") }}</b>
                                            </td>
                                            <td class="tableColumn3 text-right">
                                                <b>{{ handleNegativeNumber($n(miscSubTotal.toFixed(6), 'currency6Decimal')) }}</b>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div v-else>
                                <label>{{ $t("message.noDataAvailable") }}</label>
                            </div>
                        </tab>
                    </tabs>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import axios from "axios";
import commonJs from '@/common.js';

export default {
    name: 'UsageStatementMobile',
    data() {
        return {
            usageStatementDataLoaded: false,
            availableServiceLocations: [],
            selectedServiceLocation: '',
            servicePoints: [],
            showServicePoints: false,
            selectedServicePoint: '',
            usageStatementDates: [],
            selectedUsageStatementDate: '',
            servicePointUsageData: [],
            showUsageContentByServicePoint: false,
            usage: [],
            consumptionCharges: [],
            miscAdjustments: [],
            noUsageDataAvailable: false
        }
    },
    mounted() {
        this.getAvailableServiceLocations();       
    },
    methods: {
        getAvailableServiceLocations() {
            this.availableServiceLocations = this.$store.getters.getAvailableServiceLocations;
            if (this.availableServiceLocations.length === 0) {
                // If data for Service Locations is not available in the store, we will go ahead and fetch that information
                axios({
                    method: "GET",
                    url: this.$httpAxiosBaseUrl() + "UsageStatement/GetServiceLocation?date=" + new Date().getTime()
                }).then(result => {
                    var response = result.data;
                    if (response !== null && response !== '') {
                        this.availableServiceLocations = response;  
                        this.setDefaultServiceLocation();                      
                    }
                }, error => {
                    console.log(error);
                });
            } else {
                this.setDefaultServiceLocation();
            }        
        },
        setDefaultServiceLocation() {
            if (this.availableServiceLocations.length > 0) {
                // Select the first Service Location from our list 
                this.selectedServiceLocation = this.availableServiceLocations[0];
                this.usageStatementDataLoaded = true;

                // Get Service Points
                this.getServicePoints(this.selectedServiceLocation);
            }
        },
        getServicePoints(serviceLocation) {
            if (serviceLocation !== null && serviceLocation.ServiceLocationID > 0) {
                // If we have a valid Service Location, let's fetch our Service Points/Premises
                axios({
                    method: "POST",
                    url: this.$httpAxiosBaseUrl() + "UsageStatement/GetServicePoints",
                    data: {
                        serviceLocationId: serviceLocation.ServiceLocationID
                    }
                }).then(result => {
                    var response = result.data;
                    if (response !== null && response !== '') {
                        this.servicePoints = response;
                        if (this.servicePoints.length > 0) {
                            this.selectedServicePoint = this.servicePoints[0];
                            this.showServicePoints = true;

                            // Get Usage Statement Dates for this selected Service Point
                            this.getUsageStatementDates(this.selectedServicePoint);
                        } 
                    }
                }, error => {
                    console.log(error);
                });
            }
        },
        getUsageStatementDates(servicePoint) {
            if (servicePoint !== null && servicePoint.PremiseId > 0 && this.selectedServiceLocation !== null) {
                axios({
                    method: "POST",
                    url: this.$httpAxiosBaseUrl() + "UsageStatement/GetUsageStatementDates",
                    data: {
                        serviceLocationId: this.selectedServiceLocation.ServiceLocationID,
                        servicePointId: servicePoint.PremiseId
                    }
                }).then(result => {
                    var response = result.data;
                    if (response !== null && response !== '') {
                        this.usageStatementDates = response;
                        if (this.usageStatementDates.length > 0) {
                            this.selectedUsageStatementDate = this.usageStatementDates[0];

                            // Get Service Points Data
                            this.getServicePointsUsageData(this.selectedUsageStatementDate);
                        }
                    }
                }, error => {
                    console.log(error);
                });
            }
        },
        getServicePointsUsageData(usageStatementDate) {
            var selectedSL = this.selectedServiceLocation;
            var selectedSP = this.selectedServicePoint;
            if (selectedSL !== null && selectedSL.ServiceLocationID > 0 && selectedSP !== null && usageStatementDate !== null) {
                axios({
                    method: "POST",
                    url: this.$httpAxiosBaseUrl() + "UsageStatement/GetServicePointsUsageData",
                    data: {
                        serviceLocationId: selectedSL.ServiceLocationID,
                        servicePointId: selectedSP.PremiseId,
                        billingCycleAssignmentHistoryId: usageStatementDate.BillingCycleAssignmentHistoryID
                    }
                }).then(result => {
                    var response = result.data;
                    if (response !== null && response !== '') {
                        this.servicePointUsageData = response;
                        this.usage = this.servicePointUsageData.Usage;
                        this.consumptionCharges = this.servicePointUsageData.ConsumptionCharges;
                        this.miscAdjustments = this.servicePointUsageData.MiscAdjustments;

                        this.showUsageContentByServicePoint = true;                                           
                    }                  
                }, error => {
                    console.log(error);
                });
            }
        },
        handleNegativeNumber(num) {
            return commonJs.reformatNumber(num);
        }
    },
    computed: {
        consumptionSubTotal() {
            var rtn = 0;
            for (var index = 0, len = this.consumptionCharges.length; index < len; index++) {
                rtn += parseFloat(this.consumptionCharges[index].TotalAmount || 0);
            }          
            return rtn;
        },
        miscSubTotal() {
            var rtn = 0;
            for (var index = 0, len = this.miscAdjustments.length; index < len; index++) {
                rtn += parseFloat(this.miscAdjustments[index].TotalAmount || 0);
            }          
            return rtn;
        }
    }  
}
</script>

<style scoped>
.usage-content .tabs-component {
    margin: 0;
    font-size: 13px;
}
</style>